/*
 * custom-accordion — lean, self-contained web component.
 *
 * Recreates the Ignite <custom-accordion> behaviour used for the third
 * dropdown level (desktop) and third drawer level (mobile) without any
 * Ignite-proprietary helpers. Animates a <details> open/close by tweening the
 * panel height (the panel is summary.nextElementSibling).
 *
 * Markup:
 *   <custom-accordion>
 *     <details>
 *       <summary>Title</summary>
 *       <div class="custom-accordion__panel">...</div>
 *     </details>
 *   </custom-accordion>
 *
 * Respects prefers-reduced-motion (skips the tween).
 */
(function () {
  if (customElements.get('custom-accordion')) return;

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  class CustomAccordion extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      if (!this.details || !this.summary) return;
      this.panel = this.summary.nextElementSibling;
      this.oneAtATime = this.dataset.oneAtATime;

      this.onSummaryClick = this.handleSummaryClick.bind(this);
      this.onTransitionEnd = this.handleTransitionEnd.bind(this);
      // Capture phase so we run before any ancestor (e.g. Dawn's MenuDrawer
      // which binds a bubble-phase click listener to every <summary>) and can
      // stop it taking over our accordion summary.
      this.summary.addEventListener('click', this.onSummaryClick, true);
      this.panel.addEventListener('transitionend', this.onTransitionEnd);
    }

    disconnectedCallback() {
      this.summary?.removeEventListener('click', this.onSummaryClick, true);
      this.panel?.removeEventListener('transitionend', this.onTransitionEnd);
    }

    handleSummaryClick(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.details.open) {
        this.close();
      } else {
        if (this.oneAtATime) {
          this.closest(this.oneAtATime)
            ?.querySelectorAll('custom-accordion')
            .forEach((accordion) => {
              if (accordion !== this && accordion.details?.open) accordion.close();
            });
        }
        this.open();
      }
    }

    handleTransitionEnd(event) {
      if (event.target !== this.panel || event.propertyName !== 'height') return;
      if (this.details.classList.contains('closing')) {
        this.details.open = false;
        this.details.classList.remove('closing');
      }
      this.panel.style.height = '';
    }

    open() {
      if (this.details.open) return;
      if (REDUCED_MOTION.matches) {
        this.details.open = true;
        return;
      }
      this.panel.style.height = '0';
      this.details.open = true;
      requestAnimationFrame(() => {
        this.panel.style.height = `${this.panel.scrollHeight}px`;
      });
    }

    close() {
      if (!this.details.open) return;
      if (REDUCED_MOTION.matches) {
        this.details.open = false;
        return;
      }
      this.panel.style.height = `${this.panel.scrollHeight}px`;
      this.details.classList.add('closing');
      requestAnimationFrame(() => {
        this.panel.style.height = '0';
      });
    }
  }

  customElements.define('custom-accordion', CustomAccordion);
})();
